import { log as axiomLog } from "next-axiom";

const LOG_LEVELS = ["debug", "info", "warn", "error"] as const;

type LogLevel = (typeof LOG_LEVELS)[number];
type LogMethod = (message: string, fields?: Record<string, unknown>) => void;
type LogSink = Record<LogLevel, LogMethod>;
type IdFactory = () => string;

export type LogReference = {
  logId: string;
  errorId?: string;
};

export type SchemaSafeLogger = Record<
  LogLevel,
  (message: string, metadata?: unknown) => LogReference
>;

type TracedConsole = Record<LogLevel, (...data: unknown[]) => LogReference> & {
  log: (...data: unknown[]) => LogReference;
};

const jsonFriendlyReplacer = () => {
  const seen = new WeakSet<object>();

  return (_key: string, value: unknown) => {
    if (typeof value === "bigint") return value.toString();

    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[Circular]";
      seen.add(value);

      if (value instanceof Error)
        return {
          ...value,
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
    }

    return value;
  };
};

const metadataErrorId = (metadata: unknown) => {
  if (!metadata || typeof metadata !== "object") return undefined;

  if (
    "errorId" in metadata &&
    typeof (metadata as { errorId?: unknown }).errorId === "string"
  )
    return (metadata as { errorId: string }).errorId;

  if (
    metadata instanceof Error &&
    "id" in metadata &&
    typeof (metadata as Error & { id?: unknown }).id === "string"
  )
    return (metadata as Error & { id: string }).id;

  return undefined;
};

export const serialiseLogMessage = (
  message: string,
  metadata: unknown,
  reference: LogReference,
) => {
  try {
    return JSON.stringify(
      {
        message,
        ...reference,
        ...(metadata === undefined ? {} : { metadata }),
      },
      jsonFriendlyReplacer(),
    );
  } catch (error) {
    return JSON.stringify({
      message,
      ...reference,
      metadata: {
        serialisationError:
          error instanceof Error ? error.name : "UnknownError",
      },
    });
  }
};

export const createSchemaSafeLogger = (
  sink: LogSink,
  createId: IdFactory = () => globalThis.crypto.randomUUID(),
): SchemaSafeLogger => {
  const write = (level: LogLevel, message: string, metadata?: unknown) => {
    const reference: LogReference = {
      logId: createId(),
      ...(level === "error"
        ? { errorId: metadataErrorId(metadata) ?? createId() }
        : {}),
    };

    // The Vercel Axiom dataset is already at its field limit. Keeping
    // identifiers and application metadata inside the existing message string
    // prevents arbitrary object keys from becoming fields.* schema columns.
    sink[level](serialiseLogMessage(message, metadata, reference));
    return reference;
  };

  return {
    debug: (message, metadata) => write("debug", message, metadata),
    info: (message, metadata) => write("info", message, metadata),
    warn: (message, metadata) => write("warn", message, metadata),
    error: (message, metadata) => write("error", message, metadata),
  };
};

export const log = createSchemaSafeLogger(axiomLog);

const consoleMessage = (level: LogLevel, data: unknown[]) => {
  const [first, ...rest] = data;
  const message =
    typeof first === "string"
      ? first
      : first instanceof Error
        ? first.message
        : `Application console ${level}`;
  const metadata =
    typeof first === "string"
      ? rest.length === 0
        ? undefined
        : rest.length === 1
          ? rest[0]
          : rest
      : data.length === 1
        ? first
        : data;

  return { message, metadata };
};

export const tracedConsole: TracedConsole = {
  debug: (...data) => {
    const { message, metadata } = consoleMessage("debug", data);
    return log.debug(message, metadata);
  },
  info: (...data) => {
    const { message, metadata } = consoleMessage("info", data);
    return log.info(message, metadata);
  },
  log: (...data) => {
    const { message, metadata } = consoleMessage("info", data);
    return log.info(message, metadata);
  },
  warn: (...data) => {
    const { message, metadata } = consoleMessage("warn", data);
    return log.warn(message, metadata);
  },
  error: (...data) => {
    const { message, metadata } = consoleMessage("error", data);
    return log.error(message, metadata);
  },
};
